/* eslint-disable no-console */
import {
  type DescMessage,
  type MessageShape,
  toJson,
} from '@bufbuild/protobuf';
import {
  ConnectError,
  type Interceptor,
  type StreamResponse,
} from '@connectrpc/connect';
import { DEBUG_MODE_STORAGE_KEY } from '~/services/api/debug/debug-toggle';

let grpcDebugCallId = 0;

function messageToJson<T extends DescMessage>(
  schema: T,
  message: MessageShape<T>,
) {
  try {
    return toJson(schema, message);
  } catch {
    return message;
  }
}

const headersToObject = (headers: Headers) =>
  Object.fromEntries(headers.entries());

const describeError = (error: unknown) =>
  error instanceof ConnectError
    ? {
        code: error.code,
        rawMessage: error.rawMessage,
        metadata: headersToObject(error.metadata),
        details: error.details,
      }
    : error;

/**
 * Logs one collapsed console group. Passing `error` marks the group as failed:
 * the title turns red and the error is logged with `console.error`.
 */
function logGroup(
  title: string,
  lines: Record<string, unknown>,
  error?: unknown,
) {
  if (error === undefined) {
    console.groupCollapsed(title);
  } else {
    console.groupCollapsed(`%c${title}`, 'color: #e5484d');
  }
  Object.entries(lines).forEach(([name, value]) =>
    console.log(`${name}:`, value),
  );
  if (error !== undefined) {
    console.error('Error:', describeError(error));
  }
  console.groupEnd();
}

/**
 * Passes a stream's messages through unchanged, logging each one, then the
 * normal end of the stream or the error it failed with.
 */
async function* logStreamMessages(
  label: string,
  response: StreamResponse,
  elapsedMs: () => number,
) {
  let count = 0;

  try {
    for await (const message of response.message) {
      count += 1;
      logGroup(`${label} ← message ${count} (+${elapsedMs()}ms)`, {
        Message: messageToJson(response.method.output, message),
      });
      yield message;
    }

    logGroup(
      `${label} ✓ stream ended after ${count} message(s) (${elapsedMs()}ms)`,
      { Trailers: headersToObject(response.trailer) },
    );
  } catch (error) {
    logGroup(
      `${label} ✗ stream failed after ${count} message(s) (${elapsedMs()}ms)`,
      { Trailers: headersToObject(response.trailer) },
      error,
    );
    throw error;
  }
}

export const grpcDebugMiddleware: Interceptor = (next) => async (req) => {
  if (localStorage.getItem(DEBUG_MODE_STORAGE_KEY) !== 'true') {
    return next(req);
  }

  grpcDebugCallId += 1;
  const label = `[gRPC #${grpcDebugCallId}] ${req.method.name}`;
  const startedAt = performance.now();
  const elapsedMs = () => Math.round(performance.now() - startedAt);
  const request = req.stream
    ? '[stream]'
    : messageToJson(req.method.input, req.message);

  try {
    const response = await next(req);

    if (!response.stream) {
      logGroup(`${label} (${elapsedMs()}ms)`, {
        Request: request,
        Response: messageToJson(response.method.output, response.message),
      });
      return response;
    }

    logGroup(`${label} → stream opened (${elapsedMs()}ms)`, {
      Request: request,
      Headers: headersToObject(response.header),
    });
    return {
      ...response,
      message: logStreamMessages(label, response, elapsedMs),
    };
  } catch (error) {
    logGroup(`${label} ✗ (${elapsedMs()}ms)`, { Request: request }, error);
    throw error;
  }
};
